import { Text } from './Text'

interface CalendarEventLinkProps {
  eventTitle: string
  eventDetails: string
  location: string
  startTime: string // Format: "HH:mm"
  endTime: string // Format: "HH:mm"
  dayOfWeek: string // Example: "Sunday", "Wednesday", "Friday", "Saturday"
  className?: string
}

export default function CalendarEventLink({
  eventTitle,
  eventDetails,
  location,
  startTime,
  endTime,
  dayOfWeek,
  className,
}: CalendarEventLinkProps) {
  const getNextWeekday = (targetDay: string): Date => {
    const dayMap: { [key: string]: number } = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    }
    const today = new Date()
    const targetDayIndex = dayMap[targetDay]
    const currentDayIndex = today.getDay()
    const daysUntilNextTarget = (targetDayIndex - currentDayIndex + 7) % 7 || 7 // Ensure it’s always at least 1 day ahead
    const nextDate = new Date(today)
    nextDate.setDate(today.getDate() + daysUntilNextTarget)
    return nextDate
  }

  const formatDate = (date: Date, time: string) => {
    const [hours, minutes] = time.split(':')
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0)
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
  }

  const generateCalendarLink = () => {
    const nextTargetDate = getNextWeekday(dayOfWeek)
    const startDate = formatDate(new Date(nextTargetDate), startTime)
    const endDate = formatDate(new Date(nextTargetDate), endTime)

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      eventTitle,
    )}&dates=${startDate}/${endDate}&details=${encodeURIComponent(
      eventDetails,
    )}&location=${encodeURIComponent(location)}&trp=true&reminders=30m`
  }

  const formatDisplayDate = () => {
    const nextTargetDate = getNextWeekday(dayOfWeek)
    const options: Intl.DateTimeFormatOptions = { weekday: 'short' } // Example: "Wed"
    const weekday = new Intl.DateTimeFormat('en-US', options).format(nextTargetDate)

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':')
      const date = new Date()
      date.setHours(parseInt(hours), parseInt(minutes))
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date) // Example: "8:30 PM"
    }

    return `${weekday}, ${formatTime(startTime)} - ${formatTime(endTime)}`
  }
  return (
    <a
      href={generateCalendarLink()}
      target='_blank'
      rel='noopener noreferrer'
      title='Add to Calendar'
      className={`text-blue-500 ${className}`}
    >
      <Text.Body>{formatDisplayDate()}</Text.Body>
    </a>
  )
}
